import { useAppStore } from "@/store/app";
import {
  DEFAULT_BUFFER_DAYS,
  DEFAULT_ROLLING_DAYS,
  WEEKDAY_ORDER,
} from "@/util/constants";
import {
  from24,
  type Meridian,
  type TimeFormat,
  to24,
  type WeekdayMap,
} from "@/util/dateTimeSerialize";
import {
  dateFormatForDayjs,
  getConstructedQueryKey,
  getDateTimeGeneratedFieldName,
  getDateTimeGeneratedNameSuffix,
  type GroupField,
} from "@/util/methods";

/** All weekdays selected — seeded when available-days first apply. */
const seedAllDays = (): WeekdayMap =>
  WEEKDAY_ORDER.reduce((acc, key) => ({ ...acc, [key]: true }), {} as WeekdayMap);

/** True when no day is currently selected (undefined map or all-false). */
const noDaySelected = (days: WeekdayMap | undefined): boolean =>
  !days || Object.values(days).every((selected) => !selected);

const TIME_PREVIEW_PLACEHOLDER = "HH:MM";

const defaultPlaceholderForCollect = (
  collect: string,
  element: GroupField | undefined
): string =>
  collect === "TIME"
    ? TIME_PREVIEW_PLACEHOLDER
    : dateFormatForDayjs(element?.format, element?.separator);

const isUnset = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

const hasPatchChanges = (
  element: GroupField,
  changes: Partial<GroupField>
): boolean =>
  Object.entries(changes).some(
    ([key, value]) => element[key as keyof GroupField] !== value
  );

const generatedDateTimeNameChanges = (
  collect: string,
  element: GroupField | undefined
): Partial<GroupField> => {
  if (!element || element.id) return {};
  const generatedSuffix = getDateTimeGeneratedNameSuffix(element.label);
  if (!generatedSuffix) return {};
  const label = getDateTimeGeneratedFieldName(collect, generatedSuffix);
  if (!label) return {};

  const hiddenFieldQueryKey = getConstructedQueryKey(label);
  const currentGeneratedKey = getConstructedQueryKey(element.label);
  const changes: Partial<GroupField> = {
    label,
    hiddenFieldQueryKey,
  };

  if (
    element.fieldKey &&
    (element.fieldKey === currentGeneratedKey ||
      element.fieldKey === element.hiddenFieldQueryKey)
  ) {
    changes.fieldKey = hiddenFieldQueryKey;
  }

  return changes;
};

/**
 * Shared emit handlers for the custom Date & Time Picker settings panel.
 * Used by Forms / Surveys / Quizzes sidebars so the three surfaces cannot drift.
 *
 * `getElement` returns the reactive element the sidebar is editing — the target
 * differs per surface (`store.elements[i]` vs `store.slides[s].slideData[i]`),
 * so the caller supplies the accessor and this composable stays surface-agnostic.
 */
export function useDateTimeFieldHandlers(getElement: () => GroupField | undefined) {
  const store = useAppStore();

  // Date/time metadata (dateTimeValidation, placeholder, prefill) is persisted on
  // the location custom field as well as the form JSON, so an edit to an existing
  // custom field must flag it for the update API in BuilderHeader — marking the
  // builder dirty alone leaves the location metadata stale. `markCustomFieldUpdated`
  // sets the flag only when `element.id` exists, so freshly dropped fields stay
  // create-only. It also owns `anyUnsavedChanges` + history.
  const commit = (): void => {
    store.markCustomFieldUpdated(getElement());
  };

  const patch = (changes: Partial<GroupField>): void => {
    const element = getElement();
    if (!element) return;
    if (!hasPatchChanges(element, changes)) return;
    Object.assign(element, changes);
    commit();
  };

  const updateCollectType = (value: string): void => {
    // Type is editable only on a fresh drop; reset now-irrelevant keys so a
    // switched type never carries a stale, mismatched payload.
    const element = getElement();
    const placeholder = defaultPlaceholderForCollect(value, element);
    if (value === "TIME") {
      patch({
        ...generatedDateTimeNameChanges(value, element),
        dataType: value,
        dateMode: undefined,
        futureSub: undefined,
        dateRangeMin: undefined,
        dateRangeMax: undefined,
        days: undefined,
        placeholder,
      });
      return;
    }
    // DATE / DATE_TIME
    patch({
      ...generatedDateTimeNameChanges(value, element),
      dataType: value,
      timeMode: undefined,
      timeRangeStart: undefined,
      timeRangeStartMeridian: undefined,
      timeRangeEnd: undefined,
      timeRangeEndMeridian: undefined,
      placeholder,
    });
  };

  const updateDateMode = (value: string): void => {
    const element = getElement();
    const changes: Partial<GroupField> = { dateMode: value as GroupField["dateMode"] };
    // Available days apply for future_only / range. Seed all 7 true on first
    // apply so the field is not save-blocked by an empty day selection.
    const daysApply = value === "future_only" || value === "range";
    if (daysApply && noDaySelected(element?.days)) changes.days = seedAllDays();
    patch(changes);
  };

  const updateFutureSub = (value: string): void => {
    const element = getElement();
    const changes: Partial<GroupField> = { futureSub: value as GroupField["futureSub"] };
    // Pre-fill the design defaults as a convenience when a sub-option is picked.
    if (value === "buffer" && isUnset(element?.bufferDays)) {
      changes.bufferDays = DEFAULT_BUFFER_DAYS;
    }
    if (value === "rolling" && isUnset(element?.rollingDays)) {
      changes.rollingDays = DEFAULT_ROLLING_DAYS;
    }
    patch(changes);
  };

  const updateBuffer = (value: number): void => {
    patch({ bufferDays: value });
  };

  const updateRolling = (value: number): void => {
    patch({ rollingDays: value });
  };

  const updateDateRange = ({ which, value }: { which: "min" | "max"; value: string }): void => {
    patch(which === "min" ? { dateRangeMin: value } : { dateRangeMax: value });
  };

  const updateDateFormat = (value: string): void => {
    const element = getElement();
    if (!element) return;
    patch({ format: value, placeholder: dateFormatForDayjs(value, element.separator) });
  };

  const updateDateSeparator = (value: string): void => {
    const element = getElement();
    if (!element) return;
    patch({ separator: value, placeholder: dateFormatForDayjs(element.format, value) });
  };

  const updateDays = ({ key, checked }: { key: keyof WeekdayMap; checked: boolean }): void => {
    const element = getElement();
    const next: WeekdayMap = { ...(element?.days ?? {}), [key]: checked };
    patch({ days: next });
  };

  const updateTimeMode = (value: string): void => {
    patch({ timeMode: value as GroupField["timeMode"] });
  };

  const updateTimeRange = (payload: {
    which: "start" | "end";
    value?: string;
    meridian?: Meridian;
  }): void => {
    const { which, value, meridian } = payload;
    const hasValue = Object.prototype.hasOwnProperty.call(payload, "value");
    const hasMeridian = Object.prototype.hasOwnProperty.call(payload, "meridian");
    if (which === "start") {
      patch({
        ...(hasValue ? { timeRangeStart: value } : {}),
        ...(hasMeridian ? { timeRangeStartMeridian: meridian } : {}),
      });
    } else {
      patch({
        ...(hasValue ? { timeRangeEnd: value } : {}),
        ...(hasMeridian ? { timeRangeEndMeridian: meridian } : {}),
      });
    }
  };

  const updateTimeFormat = (value: string): void => {
    const element = getElement();
    if (!element) return;

    const previousFormat = (element.timeFormat as TimeFormat) || "24";
    const nextFormat = value as TimeFormat;
    const changes: Partial<GroupField> = { timeFormat: nextFormat };

    const convertTime = (time?: string, meridian?: Meridian) => {
      if (!time) {
        return {
          value: time,
          meridian: nextFormat === "24" ? undefined : meridian,
        };
      }
      return from24(to24(time, meridian, previousFormat), nextFormat);
    };

    const start = convertTime(
      element.timeRangeStart,
      element.timeRangeStartMeridian
    );
    changes.timeRangeStart = start.value;
    changes.timeRangeStartMeridian = start.meridian;

    const end = convertTime(element.timeRangeEnd, element.timeRangeEndMeridian);
    changes.timeRangeEnd = end.value;
    changes.timeRangeEndMeridian = end.meridian;

    const custom = convertTime(element.customTime, element.customTimeMeridian);
    changes.customTime = custom.value;
    changes.customTimeMeridian = custom.meridian;

    patch(changes);
  };

  const updateDefaultTime = (value: string): void => {
    patch({ defaultTime: value as GroupField["defaultTime"] });
  };

  const updateCustomTime = (payload: string | { value?: string; meridian?: Meridian }): void => {
    if (typeof payload === "string") {
      patch({ customTime: payload });
      return;
    }
    const hasValue = Object.prototype.hasOwnProperty.call(payload, "value");
    const hasMeridian = Object.prototype.hasOwnProperty.call(payload, "meridian");
    patch({
      ...(hasValue ? { customTime: payload.value } : {}),
      ...(hasMeridian ? { customTimeMeridian: payload.meridian } : {}),
    });
  };

  return {
    updateCollectType,
    updateDateMode,
    updateFutureSub,
    updateBuffer,
    updateRolling,
    updateDateRange,
    updateDateFormat,
    updateDateSeparator,
    updateDays,
    updateTimeMode,
    updateTimeRange,
    updateTimeFormat,
    updateDefaultTime,
    updateCustomTime,
  };
}
